import { groupBy, map, uniq } from "lodash-es";
import type {
  ChatLogFilters,
  ChatMessage,
  ChatMessageQuery,
} from "./tradeChatLogsTypes";

export interface TextSegment {
  text: string;
  match: boolean;
}

const SEARCH_TOKEN = /(-?)"([^"]*)"|(-?)(\S+)/g;

/**
 * The words and phrases a search must contain, mirroring the backend's parser:
 * `"quoted phrases"` stay whole and `-excluded` terms are dropped (they never appear in a match).
 */
export function includedSearchTerms(query: string): string[] {
  return [...query.matchAll(SEARCH_TOKEN)]
    .filter(([, phraseSign, , wordSign]) => !(phraseSign || wordSign))
    .map(([, , phrase, , word]) => phrase ?? word)
    .filter((term) => term.trim().length > 0);
}

/** Splits text into plain and matched segments, case-insensitively, for safe `<mark>` rendering. */
export function highlightSegments(
  text: string,
  terms: string[]
): TextSegment[] {
  const needles = uniq(terms.map((term) => term.toLowerCase())).filter(Boolean);
  if (!needles.length || !text) return text ? [{ text, match: false }] : [];
  const escaped = needles
    .sort((a, b) => b.length - a.length)
    .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return text
    .split(new RegExp(`(${escaped.join("|")})`, "gi"))
    .filter(Boolean)
    .map((part) => ({
      text: part,
      match: needles.includes(part.toLowerCase()),
    }));
}

/** The UTC calendar day (`YYYY-MM-DD`) of an ISO timestamp, whatever offset it was written with. */
export const utcDay = (timestamp: string): string =>
  new Date(timestamp).toISOString().slice(0, 10);

/** Messages grouped under their UTC calendar day, in the order given. */
export function groupByDay(
  messages: ChatMessage[]
): { day: string; messages: ChatMessage[] }[] {
  const groups = groupBy(messages, (message) => utcDay(message.timestamp));
  return map(
    uniq(messages.map((message) => utcDay(message.timestamp))),
    (day) => ({ day, messages: groups[day] })
  );
}

const nextDay = (day: string): string =>
  new Date(Date.parse(`${day}T00:00:00Z`) + 86_400_000)
    .toISOString()
    .slice(0, 10);

/** The API query for the viewer's filters; the inclusive `until` day becomes an exclusive bound. */
export function toMessageQuery(filters: ChatLogFilters): ChatMessageQuery {
  return {
    q: filters.q.trim() || undefined,
    conversation_id: filters.conversationId ?? undefined,
    sender: filters.sender.trim() || undefined,
    since: filters.since ?? undefined,
    until: filters.until ? nextDay(filters.until) : undefined,
    has_trade_signals:
      filters.signals === null ? undefined : filters.signals === "with",
    order: filters.order,
  };
}

/** "Tue, 1 Sep 2026" for a `YYYY-MM-DD` UTC day. */
export function formatDayLabel(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "13:07:27" in UTC — the clock traders quote against. */
export function formatClock(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString("en-GB", { timeZone: "UTC" });
}
